import { __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { getDataTestId } from '@alfalab/core-components-shared/esm';
import { useLayoutEffect_SAFE_FOR_SSR } from '@alfalab/hooks';
import { BackArrowAddon } from './components/back-arrow-addon/Component.js';
import { Closer } from './components/closer/Component.js';

var styles = {"header":"navigation-bar-private__header_9ony2","backgroundImage":"navigation-bar-private__backgroundImage_9ony2","mainLine":"navigation-bar-private__mainLine_9ony2","mainLineSticky":"navigation-bar-private__mainLineSticky_9ony2","mainLineWithImageBg":"navigation-bar-private__mainLineWithImageBg_9ony2","content":"navigation-bar-private__content_9ony2","withBothAddons":"navigation-bar-private__withBothAddons_9ony2","withCompactTitle":"navigation-bar-private__withCompactTitle_9ony2","children":"navigation-bar-private__children_9ony2","title":"navigation-bar-private__title_9ony2","contentOnBotDesktop":"navigation-bar-private__contentOnBotDesktop_9ony2","contentOnBotMobile":"navigation-bar-private__contentOnBotMobile_9ony2","subtitle":"navigation-bar-private__subtitle_9ony2","addonsWrapper":"navigation-bar-private__addonsWrapper_9ony2","rightAddons":"navigation-bar-private__rightAddons_9ony2","addon":"navigation-bar-private__addon_9ony2","bottomAddons":"navigation-bar-private__bottomAddons_9ony2","closer":"navigation-bar-private__closer_9ony2","left":"navigation-bar-private__left_9ony2","center":"navigation-bar-private__center_9ony2","trim":"navigation-bar-private__trim_9ony2"};
require('./index.css')

var ADDONS_HEIGHT = 48;
var NavigationBarPrivate = forwardRef(function (_a, ref) {
    var _b, _c, _d;
    var addonClassName = _a.addonClassName, className = _a.className, contentClassName = _a.contentClassName, closerClassName = _a.closerClassName, leftAddons = _a.leftAddons, rightAddons = _a.rightAddons, bottomAddons = _a.bottomAddons, bottomAddonsClassName = _a.bottomAddonsClassName, children = _a.children, _e = _a.align, align = _e === void 0 ? 'left' : _e, _f = _a.trim, trim = _f === void 0 ? true : _f, title = _a.title, _g = _a.titleSize, titleSize = _g === void 0 ? 'default' : _g, subtitle = _a.subtitle, hasCloser = _a.hasCloser, hasBackButton = _a.hasBackButton, backButtonClassName = _a.backButtonClassName, backButtonProps = _a.backButtonProps, dataTestId = _a.dataTestId, imageUrl = _a.imageUrl, closerIcon = _a.closerIcon, onClose = _a.onClose, view = _a.view, scrollableParentRef = _a.scrollableParentRef, sticky = _a.sticky, onBack = _a.onBack;
    var _h = useState(0), scrollTop = _h[0], setScrollTop = _h[1];
    var _j = useState({ left: 0, right: 0 }), titleMargin = _j[0], setTitleMargin = _j[1];
    var bottomContentRef = useRef(null);
    var headerRef = useRef(null);
    var mainLinePaddingTopRef = useRef('0px');
    var leftAddonsRef = useRef(null);
    var rightAddonsRef = useRef(null);
    var compactTitle = view === 'mobile' && titleSize === 'compact';
    var hasLeftPart = Boolean(leftAddons || hasBackButton);
    var hasRightPart = Boolean(rightAddons || hasCloser);
    var hasContent = Boolean(title || children);
    var withAnimation = Boolean(view === 'mobile' && hasLeftPart && sticky && !compactTitle);
    var showContentOnTop = hasContent && (compactTitle || !hasLeftPart);
    var showContentOnBot = hasContent && !compactTitle && hasLeftPart;
    var showStaticContentOnTop = !withAnimation && showContentOnTop;
    var showStaticContentOnBot = !withAnimation && showContentOnBot;
    var showAnimatedContentOnTop = withAnimation && showContentOnBot && scrollTop > ADDONS_HEIGHT;
    var showAnimatedContentOnBot = withAnimation && showContentOnBot;
    var headerPaddingTop = mainLinePaddingTopRef.current;
    useLayoutEffect_SAFE_FOR_SSR(function () {
        var _a, _b;
        if (align === 'center' && (showStaticContentOnTop || showAnimatedContentOnTop)) {
            var leftAddonsWidth = ((_a = leftAddonsRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0;
            var rightAddonsWidth = ((_b = rightAddonsRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 0;
            var marginSize_1 = Math.abs(rightAddonsWidth - leftAddonsWidth);
            var shouldAddLeftMargin_1 = rightAddonsWidth - leftAddonsWidth > 0;
            setTitleMargin(function (prev) {
                var newState = shouldAddLeftMargin_1
                    ? { left: marginSize_1, right: 0 }
                    : { left: 0, right: marginSize_1 };
                var isStateChanged = prev.left !== newState.left || prev.right !== newState.right;
                return isStateChanged ? newState : prev;
            });
        }
    }, [
        align,
        showStaticContentOnTop,
        showAnimatedContentOnTop,
        leftAddons,
        rightAddons,
        hasBackButton,
        hasCloser,
    ]);
    useEffect(function () {
        var parent = scrollableParentRef === null || scrollableParentRef === void 0 ? void 0 : scrollableParentRef.current;
        var handleScroll = function (ev) {
            var divElement = ev.target;
            setScrollTop(divElement.scrollTop);
        };
        if (withAnimation && headerRef.current) {
            mainLinePaddingTopRef.current = getComputedStyle(headerRef.current).paddingTop;
        }
        if (withAnimation && parent) {
            parent.addEventListener('scroll', handleScroll);
        }
        return function () { return parent === null || parent === void 0 ? void 0 : parent.removeEventListener('scroll', handleScroll); };
    }, [scrollableParentRef, withAnimation]);
    var renderBackButton = function () {
        var textOpacity = 1;
        if (withAnimation) {
            var height = hasContent ? ADDONS_HEIGHT : ADDONS_HEIGHT / 2;
            textOpacity = Math.max(0, 1 - scrollTop / height);
        }
        else if (compactTitle) {
            textOpacity = 0;
        }
        return (React.createElement("div", { className: cn(styles.addon, backButtonClassName) },
            React.createElement(BackArrowAddon, __assign({ "data-test-id": getDataTestId(dataTestId, 'back-button') }, backButtonProps, { textOpacity: textOpacity, view: view, onClick: onBack }))));
    };
    var renderContent = function (args) {
        var _a;
        if (args === void 0) { args = {}; }
        var extraClassName = args.extraClassName, wrapperRef = args.wrapperRef, style = args.style, hidden = args.hidden;
        return (React.createElement("div", { style: __assign(__assign({}, style), { visibility: hidden ? 'hidden' : 'visible' }), ref: wrapperRef, className: cn(styles.content, extraClassName, contentClassName, styles[align], (_a = {},
                _a[styles.trim] = trim,
                _a[styles.withCompactTitle] = view === 'mobile' && compactTitle && hasContent,
                _a)), "aria-hidden": hidden },
            children && React.createElement("div", { className: styles.children }, children),
            title && (React.createElement("div", { className: styles.title, "data-test-id": hidden ? undefined : getDataTestId(dataTestId, 'title') }, title)),
            compactTitle && subtitle && React.createElement("div", { className: styles.subtitle }, subtitle)));
    };
    var renderCloser = function () { return (React.createElement("div", { className: cn(styles.addon, styles.closer, closerClassName) },
        React.createElement(Closer, { view: view, icon: closerIcon, dataTestId: getDataTestId(dataTestId, 'closer'), onClose: onClose }))); };
    return (React.createElement("div", { ref: mergeRefs([ref, headerRef]), className: cn(styles.header, className, (_b = {}, _b[styles.backgroundImage] = imageUrl, _b)), "data-test-id": getDataTestId(dataTestId), style: __assign(__assign({}, (imageUrl && { backgroundImage: "url(".concat(imageUrl, ")") })), (withAnimation &&
            bottomContentRef.current && {
            top: -bottomContentRef.current.scrollHeight,
        })) },
        React.createElement("div", { className: cn(styles.mainLine, (_c = {},
                _c[styles.mainLineSticky] = withAnimation,
                _c[styles.mainLineWithImageBg] = imageUrl,
                _c)), style: __assign({}, (withAnimation
                ? {
                    marginTop: "-".concat(headerPaddingTop),
                    paddingTop: headerPaddingTop,
                }
                : null)) },
            hasLeftPart && (React.createElement("div", { className: styles.addonsWrapper, ref: leftAddonsRef },
                hasBackButton && renderBackButton(),
                leftAddons && (React.createElement("div", { className: cn(styles.addon, addonClassName) }, leftAddons)))),
            showStaticContentOnTop &&
                renderContent(__assign({}, (align === 'center'
                    ? {
                        style: {
                            marginLeft: titleMargin.left,
                            marginRight: titleMargin.right,
                        },
                    }
                    : null))),
            showAnimatedContentOnTop &&
                renderContent({
                    extraClassName: styles.withBothAddons,
                    style: __assign({ opacity: Math.min(1, (scrollTop - ADDONS_HEIGHT) / ADDONS_HEIGHT) }, (align === 'center'
                        ? {
                            marginLeft: titleMargin.left,
                            marginRight: titleMargin.right,
                        }
                        : null)),
                }),
            hasRightPart && (React.createElement("div", { className: cn(styles.addonsWrapper, styles.rightAddons), ref: rightAddonsRef },
                rightAddons && (React.createElement("div", { className: cn(styles.addon, addonClassName) }, rightAddons)),
                hasCloser && renderCloser()))),
        showAnimatedContentOnBot &&
            renderContent({
                wrapperRef: bottomContentRef,
                extraClassName: styles.underAddons,
                style: { opacity: Math.max(0, 1 - scrollTop / ADDONS_HEIGHT) },
                hidden: scrollTop / ADDONS_HEIGHT > 1,
            }),
        showStaticContentOnBot &&
            renderContent({
                extraClassName: cn((_d = {},
                    _d[styles.contentOnBotDesktop] = view === 'desktop',
                    _d[styles.contentOnBotMobile] = view === 'mobile',
                    _d)),
            }),
        bottomAddons && (React.createElement("div", { className: cn(styles.bottomAddons, bottomAddonsClassName) }, bottomAddons))));
});
NavigationBarPrivate.displayName = 'NavigationBarPrivate';

export { NavigationBarPrivate };
